import React, { useState, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Paperclip,
  File,
  FileImage,
  FileVideo,
  FileAudio,
  FileText,
  Download,
  X,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import axios from 'axios';
import { stripImageMetadata } from '../utils/stripImageMetadata';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

/**
 * Encrypted File Sharing
 * - Encrypts files client-side before upload
 * - Chunked upload for large files (up to 500MB)
 * - Progress tracking
 * - Resume support
 */

// Get file icon based on mime type
const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return FileImage;
  if (mimeType?.startsWith('video/')) return FileVideo;
  if (mimeType?.startsWith('audio/')) return FileAudio;
  if (mimeType?.startsWith('text/')) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Encrypt file data using AES-GCM
const encryptFileData = async (data, aesKey) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    data
  );
  
  // Combine IV + encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return combined;
};

// Decrypt file data
const decryptFileData = async (encryptedData, aesKey) => {
  const iv = encryptedData.slice(0, 12);
  const data = encryptedData.slice(12);
  
  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    data
  );
  
  return new Uint8Array(decrypted);
};

// Hash chunk for integrity verification
const hashChunk = async (data) => {
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// File Upload Component
export const FileUpload = ({ conversationId, recipientPublicKeys, onUploadComplete }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(async (event) => {
    let file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 500MB');
      return;
    }

    // EXIF / GPS / device-tag scrub for images, BEFORE encryption.
    // Lives in /utils/stripImageMetadata.js. Safe no-op for non-images.
    if (file.type?.startsWith('image/')) {
      setCurrentFile(file);
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('Stripping metadata…');
      const result = await stripImageMetadata(file);
      if (result.stripped) {
        toast.success('Metadata stripped (GPS · camera · timestamp)', {
          duration: 2500,
        });
        file = result.file;
      } else if (result.reason && !result.reason.startsWith('passthrough')) {
        // Failure-open path: tell the user we couldn't scrub but
        // we're proceeding anyway so they can decide / cancel.
        toast.warning(
          `Could not strip metadata (${result.reason.split(':')[0]}). Sending original.`,
          { duration: 3500 },
        );
      } else if (result.reason === 'passthrough:image/svg+xml') {
        toast.warning('SVG metadata not stripped. Inspect before sending.', {
          duration: 3500,
        });
      }
    }

    setCurrentFile(file);
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Encrypting file...');

    try {
      // Generate AES key for file encryption
      const aesKey = await window.crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );

      // Export AES key
      const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);

      // Encrypt AES key for each recipient
      const encryptedKeys = {};
      for (const [userId, publicKeyJwk] of Object.entries(recipientPublicKeys)) {
        const publicKey = await window.crypto.subtle.importKey(
          'jwk',
          JSON.parse(publicKeyJwk),
          { name: 'RSA-OAEP', hash: 'SHA-256' },
          false,
          ['encrypt']
        );
        const encryptedAesKey = await window.crypto.subtle.encrypt(
          { name: 'RSA-OAEP' },
          publicKey,
          rawAesKey
        );
        encryptedKeys[userId] = btoa(String.fromCharCode(...new Uint8Array(encryptedAesKey)));
      }

      // Read file as array buffer
      const fileBuffer = await file.arrayBuffer();
      
      // Encrypt the entire file
      setUploadStatus('Encrypting...');
      const encryptedData = await encryptFileData(new Uint8Array(fileBuffer), aesKey);
      
      // Calculate number of chunks
      const totalChunks = Math.ceil(encryptedData.length / CHUNK_SIZE);

      // Initialize upload
      setUploadStatus('Initializing upload...');
      const initRes = await axios.post(`${API}/files/init`, {
        filename: file.name,
        file_size: encryptedData.length,
        mime_type: file.type || 'application/octet-stream',
        encrypted_key: encryptedKeys,
        conversation_id: conversationId,
        total_chunks: totalChunks
      });

      const { file_id } = initRes.data;

      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, encryptedData.length);
        const chunk = encryptedData.slice(start, end);
        
        const chunkHash = await hashChunk(chunk);
        
        const formData = new FormData();
        formData.append('chunk_index', i.toString());
        formData.append('chunk_hash', chunkHash);
        formData.append('chunk', new Blob([chunk]));

        setUploadStatus(`Uploading chunk ${i + 1}/${totalChunks}...`);
        
        await axios.post(`${API}/files/${file_id}/chunk`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      setUploadStatus('Complete!');
      toast.success(`${file.name} sent successfully`);
      
      if (onUploadComplete) {
        onUploadComplete(file_id);
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadStatus('Failed');
    } finally {
      setIsUploading(false);
      setCurrentFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [conversationId, recipientPublicKeys, onUploadComplete]);

  return (
    <div className="file-upload">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />
      
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="text-slate-400 hover:text-primary"
        title="Send encrypted file (max 500MB)"
        data-testid="file-upload-btn"
      >
        {isUploading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Paperclip className="w-5 h-5" />
        )}
      </Button>

      {/* Upload Progress Dialog */}
      <Dialog open={isUploading} onOpenChange={() => {}}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              Encrypting & Uploading
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3">
              {React.createElement(getFileIcon(currentFile?.type), {
                className: "w-10 h-10 text-slate-400"
              })}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {currentFile?.name}
                </p>
                <p className="text-xs text-slate-500">
                  {formatFileSize(currentFile?.size || 0)}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Progress value={uploadProgress} className="h-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>{uploadStatus}</span>
                <span>{uploadProgress}%</span>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-2 rounded-lg">
              <Lock className="w-3 h-3" />
              <span>File is encrypted before upload</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// File Download Component (for messages)
export const FileMessage = ({ message, encryptedKey, privateKey, isOwn }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');

  // Parse file info from encrypted content
  let fileInfo;
  try {
    fileInfo = JSON.parse(message.encrypted_content);
  } catch {
    return null;
  }

  if (fileInfo.type !== 'file') return null;

  const FileIcon = getFileIcon(fileInfo.mime_type);

  const handleDownload = async () => {
    if (!encryptedKey || !privateKey) {
      toast.error('Cannot decrypt file - missing keys');
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);
    setDownloadStatus('Downloading...');

    try {
      // Get file metadata
      const metaRes = await axios.get(`${API}/files/${fileInfo.file_id}`);
      const fileMeta = metaRes.data;

      // Decrypt AES key
      setDownloadStatus('Decrypting...');
      const encryptedAesKeyBuffer = Uint8Array.from(atob(encryptedKey), c => c.charCodeAt(0));
      const rawAesKey = await window.crypto.subtle.decrypt(
        { name: 'RSA-OAEP' },
        privateKey,
        encryptedAesKeyBuffer
      );

      const aesKey = await window.crypto.subtle.importKey(
        'raw',
        rawAesKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      // Download all chunks
      const chunks = [];
      for (let i = 0; i < fileMeta.total_chunks; i++) {
        setDownloadStatus(`Downloading chunk ${i + 1}/${fileMeta.total_chunks}...`);
        const chunkRes = await axios.get(
          `${API}/files/${fileInfo.file_id}/chunk/${i}`,
          { responseType: 'arraybuffer' }
        );
        chunks.push(new Uint8Array(chunkRes.data));
        setDownloadProgress(Math.round(((i + 1) / fileMeta.total_chunks) * 100));
      }

      // Combine chunks
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const encryptedData = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        encryptedData.set(chunk, offset);
        offset += chunk.length;
      }

      // Decrypt file
      setDownloadStatus('Decrypting file...');
      const decryptedData = await decryptFileData(encryptedData, aesKey);

      // Create download link
      const blob = new Blob([decryptedData], { type: fileInfo.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileInfo.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDownloadStatus('Complete!');
      toast.success('File downloaded and decrypted');

    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
      setDownloadStatus('Failed');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  return (
    <div className={`file-message p-3 rounded-xl ${isOwn ? 'bg-primary/20' : 'bg-secondary/50'} max-w-xs`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isOwn ? 'bg-primary/30' : 'bg-secondary'}`}>
          <FileIcon className="w-6 h-6 text-slate-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {fileInfo.filename}
          </p>
          <p className="text-xs text-slate-500">
            {formatFileSize(fileInfo.file_size)}
          </p>
        </div>
      </div>

      {isDownloading ? (
        <div className="mt-3 space-y-2">
          <Progress value={downloadProgress} className="h-1" />
          <p className="text-xs text-slate-500">{downloadStatus}</p>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 text-xs"
          onClick={handleDownload}
          data-testid={`download-file-${fileInfo.file_id}`}
        >
          <Download className="w-3 h-3 mr-2" />
          Download & Decrypt
        </Button>
      )}

      <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400">
        <Lock className="w-3 h-3" />
        <span>E2E Encrypted</span>
      </div>
    </div>
  );
};

export default FileUpload;
